import { useGetAiChatLimitStatusQuery } from '@gonfalon/observability-graphql/hooks';
import type { GetAiChatLimitStatusQuery } from '@gonfalon/observability-graphql/operations';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';

export const AI_CHAT_LIMIT_APPROACHING_RATIO = 0.66;

export interface UseAiChatLimitArgs {
  skip?: boolean;
}

export interface UseAiChatLimitResult {
  /** Raw Apollo query data. Useful when passing through to components that take the unwrapped shape. */
  data: GetAiChatLimitStatusQuery | undefined;
  /** The aiChatLimitStatus object, or null when the query hasn't returned yet. */
  status: GetAiChatLimitStatusQuery['aiChatLimitStatus'] | null;
  loading: boolean;
  /** True when current_count has reached or exceeded a positive limit. */
  isAtLimit: boolean;
  /** True when usage is above AI_CHAT_LIMIT_APPROACHING_RATIO of a positive limit. */
  isApproachingLimit: boolean;
}

/**
 * Reads the workspace-scoped VegaAI chat-limit meter for the current project
 * (resolved via `useProjectId`) and returns the raw status plus the standard
 * derived booleans (`isAtLimit`, `isApproachingLimit`). Use this anywhere that
 * needs to gate or display the VegaAI usage limit so the threshold and shape
 * stay consistent.
 */
export function useAiChatLimit({ skip }: UseAiChatLimitArgs = {}): UseAiChatLimitResult {
  const { projectId } = useProjectId();
  const { data, loading } = useGetAiChatLimitStatusQuery({
    variables: { project_id: projectId },
    skip: skip || !projectId,
  });

  const status = data?.aiChatLimitStatus ?? null;
  const hasPositiveLimit = !!status && status.limit > 0;
  return {
    data,
    status,
    loading,
    isAtLimit: hasPositiveLimit && status.current_count >= status.limit,
    isApproachingLimit: hasPositiveLimit && status.current_count / status.limit > AI_CHAT_LIMIT_APPROACHING_RATIO,
  };
}
